import { useState, useMemo } from "react";
import { StatusPill, SectionHeader, Stat } from "./ui";
import { ArrowLeft, Building2, MapPin, Calendar, User as UserIcon, Loader2, AlertOctagon, Info } from "lucide-react";
import { CompanyContacts } from "./CompanyContacts";
import { CustomerNotes } from "./CustomerNotes";
import { useFetch } from "./api/useFetch";
import { adaptCompanyDetail, adaptStats, adaptStore, adaptDepartments, adaptUser, unwrap } from "./api/adapters";
import { isSurfaceLive, isLiveMode } from "./api/platformClient";
import { LiveBadge, SourceTag } from "./ui-badges";
import { LifecycleActionsCard } from "./LifecycleActionsCard";
import { OperatorActivity } from "./OperatorActivity";

// Render a "Not reported by ShelfLife" inline placeholder for fields the
// upstream API did not include. Never shows "0" or "—" for a missing value.
const NotReported = ({ testId }) => (
  <span
    data-testid={testId}
    title="ShelfLife did not include this field for this tenant."
    className="text-xs text-slate-400 italic"
  >
    Not reported
  </span>
);

const formatNumber = (v) =>
  typeof v === "number" && Number.isFinite(v) ? v.toLocaleString() : null;

// Take the larger of the two sources. We treat 0 as "the source did not
// report this metric meaningfully" — never as authoritative when another
// source disagrees. Returns null only when neither source provided a
// positive value.
const reconcileCount = (...sources) => {
  let best = null;
  for (const v of sources) {
    if (typeof v === "number" && Number.isFinite(v) && v > 0) {
      if (best === null || v > best) best = v;
    }
  }
  return best;
};

export const CompanyDetail = ({ client: mockClient, onBack }) => {
  const live = isLiveMode();
  const liveDetail = isSurfaceLive("COMPANY_DETAIL");
  const liveStats = isSurfaceLive("STATS");

  const { data: liveData, error: liveError, loading: liveLoading, reload: reloadCompany } = useFetch(
    liveDetail && mockClient?.id ? `/api/platform/companies/${mockClient.id}` : null
  );
  // Authoritative aggregate counts come from /stats — ShelfLife's
  // company-detail endpoint frequently returns store/user/catalog counts as 0
  // for tenants whose real values are populated only on /stats. We fetch
  // both and reconcile so the UI never lies about an empty tenant.
  const { data: statsData } = useFetch(
    liveStats && mockClient?.id ? `/api/platform/companies/${mockClient.id}/stats` : null
  );
  const stats = statsData ? adaptStats(statsData) : null;

  // Use live company shape if available; else fall back to the mock
  const client = useMemo(() => {
    if (liveDetail && liveData) {
      const companyObj = unwrap(liveData, "company") || liveData;
      const adapted = adaptCompanyDetail(companyObj || {});
      // Preserve mock fallback values for fields the live API may not include
      return {
        ...mockClient,
        ...adapted,
        // make sure nested arrays default to [] if not provided
        stores: adapted.stores.length ? adapted.stores : (mockClient?.stores || []),
        departments: adapted.departments.length ? adapted.departments : (mockClient?.departments || []),
        users: adapted.users.length ? adapted.users : (mockClient?.users || []),
      };
    }
    return mockClient;
  }, [liveDetail, liveData, mockClient]);

  // Reconcile authoritative counts. Whichever source returns the larger
  // positive value wins. Detail-array lengths are also considered so a
  // populated /stores endpoint cannot be undershadowed by a stale aggregate.
  const effStoreCount = reconcileCount(
    stats?.storeCount,
    client?.storeCount,
    client?.stores?.length
  );
  const effUserCount = reconcileCount(
    stats?.userCount,
    client?.userCount,
    client?.users?.length
  );
  const effCatalogItems = reconcileCount(
    // ShelfLife stats now exposes a strict catalog product count (or null).
    // We deliberately do NOT fall back to inventory.total here — that is
    // the inventory-batches count, a different concept.
    stats?.catalogProductCount,
    client?.catalogItems
  );
  const effDeptCount = reconcileCount(
    stats?.departmentCount,
    client?.departments?.length
  );

  const [tab, setTab] = useState("overview");
  // In live mode, Contacts and Notes are mock CRM data and are hidden from
  // the operator's tenant view.
  const tabs = [
    { key: "overview", label: "Overview" },
    ...(live ? [] : [
      { key: "contacts", label: "Contacts" },
      { key: "notes", label: "Notes" },
    ]),
    {
      key: "stores",
      label: effStoreCount ? `Stores (${effStoreCount})` : "Stores",
    },
    {
      key: "departments",
      label: effDeptCount ? `Departments (${effDeptCount})` : "Departments",
    },
    {
      key: "users",
      label: effUserCount ? `Users (${effUserCount})` : "Users",
    },
    // Phase 3C — tenant-scoped action history. Only meaningful in live mode
    // (ShelfLife's /api/platform/action-history requires company_id).
    ...(live ? [{ key: "activity", label: "Activity" }] : []),
  ];

  if (!client) return null;

  // Truthful tile values: prefer reconciled counts (stats authoritative).
  // Fall back to "Not reported" in live mode, "—" or "0" in mock mode.
  const storeCountDisplay = effStoreCount !== null
    ? formatNumber(effStoreCount)
    : (live ? <NotReported testId="not-reported-stores" /> : "—");
  const userCountDisplay = effUserCount !== null
    ? formatNumber(effUserCount)
    : (live ? <NotReported testId="not-reported-users" /> : "—");
  const catalogItemsDisplay = effCatalogItems !== null
    ? formatNumber(effCatalogItems)
    : (live ? <NotReported testId="not-reported-catalog-items" /> : "0");
  const planDisplay = client.plan && client.plan !== "—"
    ? client.plan
    : (live ? <NotReported testId="not-reported-plan" /> : "—");

  return (
    <div data-testid={`company-detail-${client.id}`}>
      <button
        type="button"
        data-testid="company-detail-back"
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-[#0d3b66] mb-4"
      >
        <ArrowLeft className="h-4 w-4" /> All clients
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">
              {client.name}
            </h1>
            <StatusPill status={client.status} testId="company-detail-status" />
            <LiveBadge live={liveDetail} />
          </div>
          <div className="mt-1 text-sm text-slate-500 flex items-center gap-4 flex-wrap">
            <span className="inline-flex items-center gap-1.5">
              <Building2 className="h-3.5 w-3.5" /> {client.id}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <MapPin className="h-3.5 w-3.5" /> {client.headquarters || (live ? <NotReported testId="not-reported-hq" /> : "—")}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5" /> Created {client.createdAt || (live ? <NotReported testId="not-reported-created" /> : "—")}
            </span>
            <SourceTag live={liveDetail && !liveError} />
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs uppercase tracking-wide text-slate-500">Last activity</div>
          <div className="text-sm font-medium text-slate-900">
            {client.lastActivity && client.lastActivity !== "—"
              ? client.lastActivity
              : (live ? <NotReported testId="not-reported-last-activity" /> : "—")}
          </div>
        </div>
      </div>

      {liveDetail && liveLoading && (
        <InlineLoading label="Loading company…" />
      )}
      {liveDetail && liveError && (
        <InlineError error={liveError} prefix="Company detail" />
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Stat label="Stores" value={storeCountDisplay} testId="stat-stores" />
        <Stat label="Users" value={userCountDisplay} testId="stat-users" />
        <Stat
          label="Catalog product count"
          value={catalogItemsDisplay}
          sub={
            client.catalogStatus
              ? <StatusPill status={client.catalogStatus} />
              : (live ? <NotReported testId="not-reported-catalog-status" /> : null)
          }
          testId="stat-catalog"
        />
        <Stat
          label="Plan"
          value={planDisplay}
          sub={
            client.billingStatus
              ? <StatusPill status={client.billingStatus} />
              : (live ? <NotReported testId="not-reported-billing-status" /> : null)
          }
          testId="stat-plan"
        />
      </div>

      <div className="border-b border-slate-200 mb-4 flex gap-1 flex-wrap">
        {tabs.map((t) => (
          <button
            type="button"
            key={t.key}
            data-testid={`company-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t.key
                ? "border-[#0d3b66] text-[#0d3b66]"
                : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <>
          <Overview client={client} live={live} catalogItems={effCatalogItems} />
          <div className="mt-4">
            <LifecycleActionsCard
              company={client}
              onArchived={reloadCompany}
              onUnarchived={reloadCompany}
              onDeleted={onBack}
            />
          </div>
        </>
      )}
      {tab === "contacts" && !live && <CompanyContacts entityId={client.id} />}
      {tab === "notes" && !live && <CustomerNotes entityId={client.id} />}
      {tab === "stores" && (
        <StoresTab
          companyId={client.id}
          fallback={client.stores || []}
          expectedCount={effStoreCount}
        />
      )}
      {tab === "departments" && (
        <DepartmentsTab
          companyId={client.id}
          fallback={client.departments || []}
          expectedCount={effDeptCount}
        />
      )}
      {tab === "users" && (
        <UsersTab
          companyId={client.id}
          fallback={client.users || []}
          expectedCount={effUserCount}
        />
      )}
      {tab === "activity" && live && (
        <div data-testid="company-tab-activity-content">
          <OperatorActivity companyId={client.id} compact />
        </div>
      )}
    </div>
  );
};

const Overview = ({ client, live, catalogItems }) => (
  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
    <div className="lg:col-span-2 rounded-xl border border-slate-200 bg-white p-5">
      <SectionHeader title="Company information" />
      <dl className="grid grid-cols-2 gap-y-3 text-sm">
        <dt className="text-slate-500">Legal name</dt>
        <dd className="text-slate-900 font-medium">{client.legalName || client.name}</dd>
        <dt className="text-slate-500">Tenant ID</dt>
        <dd className="text-slate-900 font-mono text-xs">{client.id}</dd>
        <dt className="text-slate-500">Headquarters</dt>
        <dd className="text-slate-900">
          {client.headquarters && client.headquarters !== "—"
            ? client.headquarters
            : (live ? <NotReported /> : "—")}
        </dd>
        <dt className="text-slate-500">Account owner</dt>
        <dd className="text-slate-900 inline-flex items-center gap-1.5">
          <UserIcon className="h-3.5 w-3.5" />
          {client.accountOwner && client.accountOwner !== "—"
            ? client.accountOwner
            : (live ? <NotReported /> : "—")}
        </dd>
        <dt className="text-slate-500">Created</dt>
        <dd className="text-slate-900">
          {client.createdAt && client.createdAt !== "—"
            ? client.createdAt
            : (live ? <NotReported /> : "—")}
        </dd>
        <dt className="text-slate-500">Status</dt>
        <dd><StatusPill status={client.status} /></dd>
      </dl>
      {client.notes && (
        <div className="mt-5 pt-4 border-t border-slate-100">
          <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Account notes</div>
          <p className="text-sm text-slate-700 leading-relaxed">{client.notes}</p>
        </div>
      )}
    </div>

    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <SectionHeader title="Catalog product count" />
      <div className="space-y-3 text-sm">
        <Row
          label="Status"
          value={
            client.catalogStatus
              ? <StatusPill status={client.catalogStatus} />
              : (live ? <NotReported /> : <StatusPill status="not_started" />)
          }
        />
        <Row
          label="Products"
          value={
            (typeof catalogItems === "number" && catalogItems > 0
              ? catalogItems.toLocaleString()
              : (formatNumber(client.catalogItems) ?? (live ? <NotReported /> : "0")))
          }
        />
        <Row
          label="Last activity"
          value={
            client.lastActivity && client.lastActivity !== "—"
              ? client.lastActivity
              : (live ? <NotReported /> : "—")
          }
        />
      </div>
    </div>
  </div>
);

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between">
    <span className="text-slate-500">{label}</span>
    <span className="text-slate-900 font-medium">{value}</span>
  </div>
);

// A small banner used when an aggregate count (from /stats) disagrees with
// the number of row-level records returned by a detail endpoint. Renders
// the exact reported number — never fabricates row data.
const DetailMismatch = ({ expected, kind, endpoint, testId }) => (
  <div
    data-testid={testId}
    className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 mb-3 flex items-start gap-2 text-sm text-amber-900"
  >
    <Info className="h-4 w-4 mt-0.5 shrink-0" />
    <span>
      ShelfLife reports <strong>{expected.toLocaleString()}</strong> {kind} for
      this tenant, but the <span className="font-mono">{endpoint}</span> endpoint
      returned no {kind.replace(/s$/, "")} rows. The detail-row endpoint may
      not yet be implemented on the ShelfLife side for this tenant.
    </span>
  </div>
);

const StoresTab = ({ companyId, fallback, expectedCount }) => {
  const live = isSurfaceLive("STORES");
  const { data, error, loading } = useFetch(
    live && companyId ? `/api/platform/companies/${companyId}/stores` : null
  );
  const list = unwrap(data, "stores");
  const stores = live && Array.isArray(list) ? list.map(adaptStore) : fallback;
  const showMismatch =
    live && !loading && !error &&
    typeof expectedCount === "number" && expectedCount > 0 &&
    stores.length === 0;

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <SourceTag live={live && !error} />
        <LiveBadge live={live} />
      </div>
      {live && loading && <InlineLoading label="Loading stores…" />}
      {live && error && <InlineError error={error} prefix="Stores" />}
      {showMismatch && (
        <DetailMismatch
          expected={expectedCount}
          kind="stores"
          endpoint={`GET /api/platform/companies/${companyId}/stores`}
          testId="stores-detail-mismatch"
        />
      )}
      {(!live || (!loading && !error)) && <StoresTable stores={stores} />}
    </div>
  );
};

const StoresTable = ({ stores }) => (
  <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
    <table className="w-full text-sm" data-testid="stores-table">
      <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
        <tr>
          <th className="text-left px-4 py-3">Store</th>
          <th className="text-left px-4 py-3">Address</th>
          <th className="text-right px-4 py-3">Departments</th>
          <th className="text-right px-4 py-3">Users</th>
          <th className="text-left px-4 py-3">Status</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {stores.length === 0 && (
          <tr><td colSpan={5} className="px-4 py-6 text-center text-sm text-slate-500">No store rows returned.</td></tr>
        )}
        {stores.map((s) => (
          <tr key={s.id} className="hover:bg-slate-50">
            <td className="px-4 py-3 font-medium text-slate-900">{s.name}</td>
            <td className="px-4 py-3 text-slate-600">{s.address}</td>
            <td className="px-4 py-3 text-right tabular-nums">{s.departments}</td>
            <td className="px-4 py-3 text-right tabular-nums">{s.users}</td>
            <td className="px-4 py-3"><StatusPill status={s.status} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const DepartmentsTab = ({ companyId, fallback, expectedCount }) => {
  const live = isSurfaceLive("DEPARTMENTS");
  const { data, error, loading } = useFetch(
    live && companyId ? `/api/platform/companies/${companyId}/departments` : null
  );
  const list = unwrap(data, "departments");
  const departments = live && Array.isArray(list) ? adaptDepartments(list) : fallback;
  const showMismatch =
    live && !loading && !error &&
    typeof expectedCount === "number" && expectedCount > 0 &&
    departments.length === 0;

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <SourceTag live={live && !error} />
        <LiveBadge live={live} />
      </div>
      {live && loading && <InlineLoading label="Loading departments…" />}
      {live && error && <InlineError error={error} prefix="Departments" />}
      {showMismatch && (
        <DetailMismatch
          expected={expectedCount}
          kind="departments"
          endpoint={`GET /api/platform/companies/${companyId}/departments`}
          testId="departments-detail-mismatch"
        />
      )}
      {(!live || (!loading && !error)) && (
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="flex flex-wrap gap-2" data-testid="departments-list">
            {departments.length === 0 && (
              <span className="text-sm text-slate-500">No department rows returned.</span>
            )}
            {departments.map((d) => (
              <span
                key={d}
                className="inline-flex items-center px-2.5 py-1 rounded-md bg-slate-100 text-slate-700 text-sm"
              >
                {d}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const UsersTab = ({ companyId, fallback, expectedCount }) => {
  const live = isSurfaceLive("USERS");
  const { data, error, loading } = useFetch(
    live && companyId ? `/api/platform/companies/${companyId}/users` : null
  );
  // Phase 3D resilience: also fetch the stores list so we can resolve
  // store_id → name when the user payload omits storeName. Failure to
  // load stores is non-fatal — rows just fall through to "— (orphaned)".
  const { data: storesData } = useFetch(
    live && companyId ? `/api/platform/companies/${companyId}/stores` : null
  );
  const storesMap = useMemo(() => {
    const m = new Map();
    const list = storesData ? unwrap(storesData, "stores") : null;
    if (Array.isArray(list)) {
      for (const raw of list) {
        const s = adaptStore(raw);
        if (s && s.id) m.set(s.id, s);
      }
    }
    return m;
  }, [storesData]);

  const list = unwrap(data, "users");
  const users = live && Array.isArray(list) ? list.map(adaptUser) : fallback;

  // Split into active vs inactive so deactivated cleanup artefacts don't
  // sit next to real users masquerading as "pending".
  const activeUsers = users.filter((u) => u.isActive !== false && u.status !== "inactive");
  const inactiveUsers = users.filter((u) => u.isActive === false || u.status === "inactive");

  const showMismatch =
    live && !loading && !error &&
    typeof expectedCount === "number" && expectedCount > 0 &&
    users.length === 0;

  const [showInactive, setShowInactive] = useState(false);

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <SourceTag live={live && !error} />
        <LiveBadge live={live} />
      </div>
      {live && loading && <InlineLoading label="Loading users…" />}
      {live && error && <InlineError error={error} prefix="Users" />}
      {showMismatch && (
        <DetailMismatch
          expected={expectedCount}
          kind="users"
          endpoint={`GET /api/platform/companies/${companyId}/users`}
          testId="users-detail-mismatch"
        />
      )}
      {(!live || (!loading && !error)) && (
        <>
          <UsersTable users={activeUsers} storesMap={storesMap} testId="users-table-active" emptyLabel="No active users." />

          {inactiveUsers.length > 0 && (
            <div className="mt-6" data-testid="users-inactive-section">
              <button
                type="button"
                data-testid="users-inactive-toggle"
                onClick={() => setShowInactive((v) => !v)}
                className="w-full flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-100 transition-colors"
              >
                <span>
                  Inactive / deactivated users ({inactiveUsers.length})
                </span>
                <span className="text-xs text-slate-500">
                  {showInactive ? "Hide" : "Show"}
                </span>
              </button>
              {showInactive && (
                <div className="mt-3 space-y-2">
                  <p className="text-xs text-slate-500 italic">
                    Inactive users are retained by ShelfLife for audit history and
                    are not counted as active users.
                  </p>
                  <UsersTable
                    users={inactiveUsers}
                    storesMap={storesMap}
                    testId="users-table-inactive"
                    emptyLabel="No inactive users."
                    dim
                  />
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Phase 3D resilience: resolve a user's store label without ever exposing
// a raw UUID to the operator.
//   1. user.storeName from ShelfLife          → use it
//   2. storesMap.get(user.storeId) has a name → use it
//   3. user.storeId exists but no match       → "— (orphaned)"
//   4. user has no store at all               → "—"
const resolveStoreLabel = (user, storesMap) => {
  if (user.storeName) return user.storeName;
  if (user.storeId) {
    const s = storesMap && storesMap.get && storesMap.get(user.storeId);
    if (s && s.name) return s.name;
    return "— (orphaned)";
  }
  return "—";
};

const UsersTable = ({ users, storesMap, testId, emptyLabel, dim }) => (
  <div
    className={`rounded-xl border border-slate-200 bg-white overflow-hidden ${dim ? "opacity-90" : ""}`}
  >
    <table className="w-full text-sm" data-testid={testId}>
      <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
        <tr>
          <th className="text-left px-4 py-3">Name</th>
          <th className="text-left px-4 py-3">Email</th>
          <th className="text-left px-4 py-3">Role</th>
          <th className="text-left px-4 py-3">Store</th>
          <th className="text-left px-4 py-3">Status</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {users.length === 0 && (
          <tr>
            <td colSpan={5} className="px-4 py-6 text-center text-sm text-slate-500">
              {emptyLabel || "No user rows returned."}
            </td>
          </tr>
        )}
        {users.map((u) => {
          const storeLabel = resolveStoreLabel(u, storesMap);
          const isOrphaned = storeLabel === "— (orphaned)";
          return (
            <tr key={u.id} className={`hover:bg-slate-50 ${dim ? "text-slate-500" : ""}`}>
              <td className="px-4 py-3 font-medium">{u.name}</td>
              <td className="px-4 py-3">{u.email}</td>
              <td className="px-4 py-3">{u.role}</td>
              <td
                className={`px-4 py-3 ${isOrphaned ? "text-slate-400 italic" : ""}`}
                title={isOrphaned ? `store_id ${u.storeId} not in this tenant's stores list` : undefined}
                data-testid={isOrphaned ? `user-store-orphaned-${u.id}` : undefined}
              >
                {storeLabel}
              </td>
              <td className="px-4 py-3"><StatusPill status={u.status} /></td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

const InlineLoading = ({ label }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-6 text-center mb-3">
    <Loader2 className="mx-auto h-5 w-5 text-slate-400 animate-spin" />
    <p className="mt-2 text-xs text-slate-500">{label}</p>
  </div>
);

const InlineError = ({ error, prefix }) => (
  <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 mb-3 text-sm">
    <div className="flex items-center gap-2 text-rose-800 font-semibold">
      <AlertOctagon className="h-4 w-4" />
      {prefix}: {error.kind === "forbidden" ? "Not authorized" :
                error.kind === "unauthorized" ? "Session expired" :
                error.kind === "network" ? "Cannot reach preview API" :
                error.message}
    </div>
  </div>
);
